import { ArrowLeft } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useContextProvider } from "../components/layout/ThemeProvide.jsx";
import { Button } from "../components/ui/Button.jsx";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/Card.jsx";
import { Input } from "../components/ui/Input.jsx";
import { Label } from "../components/ui/Label.jsx";
import { useToast } from "../hooks/useToast.js";
import { sendOTP, verifyOTP } from "../services/authService.js";

export default function VerifyOTP() {
  const navigate = useNavigate();
  const { email, otpSent, setOtpVerified } = useContextProvider();
  const [otp, setOtp] = useState("");
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [isResendingOtp, setIsResendingOtp] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const { toast } = useToast();

  useEffect(() => {
    if (!email || !otpSent) {
      navigate("/forgot-password");
    }
  }, [email, otpSent, navigate]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setInterval(() => setCooldown((prev) => prev - 1), 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  const handleVerifyOtp = async () => {
    if (!otp) {
      toast({
        title: "Enter OTP",
        description: "Please enter the OTP received on your email.",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsVerifyingOtp(true);
      const res = await verifyOTP({ email, otp });
      if (res?.success) {
        setOtpVerified(true);
        toast({
          title: "OTP Verified",
          description: "OTP verified successfully!",
        });
        navigate("/set-password");
      } else {
        toast({
          title: "Invalid OTP",
          description: "Check Mail for Valid OTP",
          variant: "destructive",
        });
      }
    } catch (err) {
      toast({
        title: "Error",
        description: err.message || "Unable to verify OTP",
        variant: "destructive",
      });
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const handleResendOtp = async () => {
    if (!email) return;
    try {
      setIsResendingOtp(true);
      const res = await sendOTP({ email });
      if (res?.success) {
        toast({
          title: "OTP Sent",
          description: "New OTP has been sent to your email.",
        });
        setCooldown(30);
      } else {
        toast({
          title: "Failed to resend OTP",
          description: "Please try again later.",
          variant: "destructive",
        });
      }
    } catch (err) {
      toast({
        title: "Error",
        description: err.message || "Unable to resend OTP",
        variant: "destructive",
      });
    } finally {
      setIsResendingOtp(false);
    }
  };

  return (
    <div className="flex flex-col justify-between max-h-[100dvh] min-h-[100dvh] p-4 space-y-4">
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(-1)}
          className="rounded-full"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <h1 className="text-2xl font-bold">Enter Your OTP</h1>
      </div>

      <form className="space-y-4" onSubmit={(e) => e.preventDefault()}>
        <Card className="border-none shadow-sm">
          <CardHeader className="p-0">
            <CardTitle className="text-lg">OTP Information</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                placeholder="email@nipralo.com"
                value={email}
                disabled
                required
              />
            </div>
            <div className="mt-2 space-y-2">
              <Label htmlFor="otp">Enter OTP</Label>
              <Input
                id="otp"
                placeholder="Enter OTP"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                maxLength={6}
              />
            </div>
          </CardContent>
        </Card>

        <Card className="border-none shadow-sm">
          <CardContent className="space-y-4">
            <div className="flex flex-row flex-wrap w-full gap-3">
              <Button
                type="button"
                variant="outline"
                className="flex-1 h-8 text-xs rounded-full"
                onClick={handleResendOtp}
                disabled={isResendingOtp || cooldown > 0}
              >
                {isResendingOtp
                  ? "Resending..."
                  : cooldown > 0
                  ? `Resend in ${cooldown}s`
                  : "Resend OTP"}
              </Button>

              <Button
                type="button"
                className="flex-1 h-8 text-xs rounded-full bg-gradient-to-r from-[#00a8b4fa] to-[#00c8d391]"
                onClick={handleVerifyOtp}
                disabled={isVerifyingOtp}
              >
                {isVerifyingOtp ? "Verifying..." : "Verify OTP"}
              </Button>
            </div>
          </CardContent>
        </Card>
      </form>
      <div></div>
    </div>
  );
}
